import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { COMBOBOX_FILTER_COMPACT, HIDE_ON_MOBILE } from '../../theme/responsive'

import { useGetSaleReferenceRowsQuery } from '../../store/api/saleContractApi'

import SaleReferenceGrid from './SaleReferenceGrid'
import {
  SALE_REFERENCE_PAGE_CONFIGS,
  SALE_REFERENCE_PAGE_ORDER,
  type SaleReferencePageKey,
} from './saleReferenceConfig'

export default function SaleReferencesView() {
  const [pageKey, setPageKey] = useState<SaleReferencePageKey>('ValueUpdates')
  const [searchByPage, setSearchByPage] = useState<Partial<Record<SaleReferencePageKey, string>>>({})

  const page = SALE_REFERENCE_PAGE_CONFIGS[pageKey]
  const search = searchByPage[pageKey] ?? ''

  const queryArg = useMemo(
    () => ({
      pageKey,
      limit: page.limit ?? 5000,
    }),
    [page.limit, pageKey],
  )

  const { data, isLoading, error, refetch } = useGetSaleReferenceRowsQuery(
    queryArg,
    // Mỗi lần đổi tab Sales Contract → component remount → refetch lại data.
    { refetchOnMountOrArgChange: true },
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 0.25 }}
      >
        <Box sx={HIDE_ON_MOBILE}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            Sale References
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Access switchboard parity for the 13 reference pages.
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ ml: 'auto' }}>
          {(() => {
            // Skill /convert-form: combo → Autocomplete (anti-pattern #12 cấm
            // <TextField select>). PageKey là code internal nên dropdown chỉ
            // display label (không format "<CODE> — <NAME>" vì code không
            // user-meaningful — pattern hardcoded enum trong skill).
            const pageOptions = SALE_REFERENCE_PAGE_ORDER.map((key) => ({
              value: key,
              label: SALE_REFERENCE_PAGE_CONFIGS[key].label,
            }))
            const selectedOption = pageOptions.find((o) => o.value === pageKey) ?? undefined
            return (
              <Autocomplete
                size="small"
                options={pageOptions}
                value={selectedOption}
                onChange={(_, v) => v && setPageKey(v.value)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                handleHomeEndKeys
                autoHighlight
                selectOnFocus
                sx={COMBOBOX_FILTER_COMPACT}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Go to Page"
                    InputLabelProps={{ shrink: true }}
                    // readOnly trên <input>: mobile KHÔNG bung keyboard khi tap
                    // (user chỉ pick từ dropdown, không cần gõ). Autocomplete
                    // dropdown vẫn mở qua click bình thường.
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            )
          })()}
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <SaleReferenceGrid
          page={page}
          fillHeight   // parent bounded (Card→CardContent→view flex col) → bảng fill, bỏ khoảng trống
          rows={data?.items ?? []}
          search={search}
          onSearchChange={(value) =>
            setSearchByPage((prev) => ({
              ...prev,
              [pageKey]: value,
            }))
          }
          loading={isLoading}
          error={error}
          onRetry={() => {
            void refetch()
          }}
        />
      </Box>
    </Box>
  )
}
